import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Autocomplete, Box, CircularProgress, Switch, TextField, Typography,
} from '@mui/material'
import ClientService from '../../services/ClientService'
import ClientReportAccessService from '../../services/ClientReportAccessService'
import GlobalTip from '../../providers/GlobalTip'
import SectionTitle from '../../components/common/SectionTitle'

// Seconds a toast stays on screen. GlobalTip's second argument is a duration in
// seconds, not a severity — passing a string here silently yields NaN and the
// toast vanishes instantly.
const NOTICE_SECONDS = 5

const clientLabel = (client) => [
  client?.ClientCode ? `[${client.ClientCode}]` : '',
  client?.Name,
  client?.Id ? `(#${client.Id})` : '',
].filter(Boolean).join(' ')

/**
 * Admin Tools → Reports.
 *
 * Turning a report on copies its GLOBAL/DEFAULT `filters` rows from a client
 * that already has the report working, which is the manual SQL support used to
 * run by hand. Turning it off removes those rows again.
 */
export default function ClientReportsManagement() {
  const tip = GlobalTip.useMessage()
  const [clients, setClients] = useState([])
  const [client, setClient] = useState(null)
  const [sourceClient, setSourceClient] = useState(null)
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [pendingReport, setPendingReport] = useState(null)

  useEffect(() => {
    ClientService.listClientsSimple()
      .then((result) => setClients(Array.isArray(result) ? result : []))
      .catch(() => setClients([]))
  }, [])

  // Changing either client fires a reload, so responses can land out of order —
  // only the newest request is allowed to write to state.
  const latestRequest = useRef(0)

  const load = useCallback(async () => {
    const requestId = ++latestRequest.current
    if (!client?.Id) {
      setGroups([])
      return
    }
    setLoading(true)
    try {
      const result = await ClientReportAccessService.list({
        client_id: Number(client.Id),
        source_client_id: sourceClient?.Id ? Number(sourceClient.Id) : null,
      })
      if (requestId !== latestRequest.current) return
      setGroups(Array.isArray(result?.groups) ? result.groups : [])
    } catch (error) {
      if (requestId !== latestRequest.current) return
      setGroups([])
      tip.show(error?.response?.data?.message || 'Unable to load reports for this client', NOTICE_SECONDS)
    } finally {
      if (requestId === latestRequest.current) setLoading(false)
    }
  }, [client, sourceClient, tip])

  useEffect(() => { load() }, [load])

  const toggleReport = async (report, enabled) => {
    // Only PDF-capable reports have settings to copy; the rest are visibility-only.
    const needsSource = enabled && report.requires_source_client
    if (needsSource && !sourceClient?.Id) {
      tip.show(`${report.title} needs a "Copy settings from" client before it can be turned on`, NOTICE_SECONDS)
      return
    }
    setPendingReport(report.report_name)
    try {
      await ClientReportAccessService.toggle({
        client_id: Number(client.Id),
        source_client_id: needsSource ? Number(sourceClient.Id) : null,
        report_name: report.report_name,
        enabled,
      })
      tip.showSuccess(`${report.title} ${enabled ? 'enabled' : 'disabled'} for ${client.Name}`, NOTICE_SECONDS)
      await load()
    } catch (error) {
      tip.show(error?.response?.data?.message || `Unable to update ${report.title}`, NOTICE_SECONDS)
    } finally {
      setPendingReport(null)
    }
  }

  return <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
    <SectionTitle
      label="Reports"
      subLabel="Turn a client's reports on or off by copying them from a client that already has them working."
    />

    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2, mt: 3 }}>
      <Autocomplete
        options={clients}
        getOptionLabel={clientLabel}
        isOptionEqualToValue={(option, value) => Number(option.Id) === Number(value?.Id)}
        value={client}
        onChange={(_, selected) => setClient(selected)}
        renderInput={(params) => <TextField {...params} label="Client" placeholder="Search clients" helperText="The client whose reports you are changing" />}
      />
      <Autocomplete
        options={clients.filter((option) => Number(option.Id) !== Number(client?.Id))}
        getOptionLabel={clientLabel}
        isOptionEqualToValue={(option, value) => Number(option.Id) === Number(value?.Id)}
        value={sourceClient}
        onChange={(_, selected) => setSourceClient(selected)}
        renderInput={(params) => <TextField {...params} label="Copy settings from" placeholder="Search clients" helperText="A client whose reports already work — required to turn a report on" />}
      />
    </Box>

    {!client && <Typography sx={{ mt: 4 }} color="text.secondary">
      Select a client to see which reports are turned on.
    </Typography>}

    {client && loading && <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>}

    {client && !loading && groups.map((group) => (
      <Box
        key={group.report_type}
        role="group"
        aria-label={group.label}
        sx={{ mt: 3 }}
      >
        <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', color: '#667085' }}>
          {group.label}
        </Typography>
        <Box sx={{ mt: 1, display: 'grid', gap: 1 }}>
          {group.reports.length === 0 && <Typography variant="body2" color="text.secondary">No reports in this group.</Typography>}
          {group.reports.map((report) => (
            <Box
              key={report.report_key}
              sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, bgcolor: '#fff', display: 'flex', alignItems: 'center', gap: 2 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={700}>{report.title}</Typography>
                {report.description && <Typography variant="body2" color="text.secondary">{report.description}</Typography>}
                {sourceClient && report.available_on_source === false && !report.enabled && (
                  <Typography variant="caption" color="warning.main" display="block">
                    Not set up on {sourceClient.Name} — pick a different source client.
                  </Typography>
                )}
                {report.enabled && report.pdf_provisioned === false && (
                  <Typography variant="caption" color="warning.main" display="block">
                    Visible to the client, but its PDF settings are missing — turn it off and back on
                    using a source client that has it working.
                  </Typography>
                )}
                {!report.requires_source_client && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    No PDF settings to copy — no source client needed.
                  </Typography>
                )}
              </Box>
              <Switch
                checked={Boolean(report.enabled)}
                disabled={pendingReport === report.report_name}
                onChange={(event) => toggleReport(report, event.target.checked)}
                inputProps={{ 'aria-label': `${report.title} enabled` }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    ))}
  </Box>
}
